import type { Qubic, Subscription } from "api";
import type { KindTab } from "components/Store";
import { formatShortDate } from "utils/date.utils";

export type LimitNotice = {
  title: string;
  text: string;
};

const EXHAUSTED: Record<string, string> = {
  calls: "monthly limit reached",
  unique: "unique shipments limit reached",
  both: "monthly call and unique shipment limits reached",
};

/**
 * The `.pc-err` card: the gateway is refusing this tool's calls until the
 * counter starts over. Built from what the back office reports — which axes
 * ran out and when they reset — since the 429 itself never reaches it.
 *
 * An expired limit is not one: renewing it is the fix, and waiting for the
 * reset date would not bring anything back, so the card stays out.
 */
export const limitNotice = (subscription: Subscription): LimitNotice | null => {
  const { reached_on: reached, resets_on: resetsOn } = subscription.limit;

  if (reached.length === 0 || subscription.status === "expired") return null;

  const both = reached.includes("calls") && reached.includes("unique");
  const axis = both ? "both" : reached[0];

  return {
    title: `This API is not responding — ${EXHAUSTED[axis]}`,
    text: `Requests are rejected until the ${both ? "limits reset" : "limit resets"} on ${formatShortDate(resetsOn)}. Raise the limit to bring the integration back online.`,
  };
};

/**
 * The kind switch as My Qubics draws it. Only the API side has a count: which
 * widgets a company runs is not recorded anywhere, and a 0 there would claim
 * a fact nobody has.
 */
export const myQubicsTabs = (apiCount: number): KindTab[] => [
  {
    kind: "api",
    title: "API & MCP Qubics",
    text: "Manage your Qubics connected through API or MCP.",
    count: apiCount,
  },
  { kind: "web", title: "Web Qubics", text: "Manage your active web apps and widgets." },
];

/** A row's catalogue product, for its cube and customer-facing name. */
export const productOf = (subscription: Subscription, qubics: Qubic[]): Qubic | undefined =>
  qubics.find((qubic) => qubic.slug === subscription.product);
